import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Checkbox, Divider, Icon, Input, Radio, Select } from "semantic-ui-react";
import { toast } from "../../..";
import { deleteAttachment, getSearchableIcsProps } from "../../../api/certificates";
import { DateInput } from "../../../components/DateInput/DateInput";
import { FileDropzone } from "../../../components/Dropzone/FileDropzone";
import { TranslateCountryProps, TranslateProps } from "../../../i18n/TranslateProps";
import {
    applicationCategoryOptions,
    CertificateType,
    conditionOptions,
    drivePowerOptions,
    ICertificate,
    loadCapacityOptions,
    SearchableIcs,
    vehicleLengthOptions,
} from "../../../models/certificate";
import {
    constructDate,
    dateInit,
    deconstructDate,
    examinationOrPaymentToDropdownProp,
    getTodayDate,
} from "../../../models/common";
import { AppState } from "../../../store/configureStore";

type Props = {
    certificate: ICertificate;
    setCertificate: Dispatch<SetStateAction<ICertificate>>;
    setAttachment1?: Dispatch<SetStateAction<File | undefined>>;
    setAttachment2?: Dispatch<SetStateAction<File | undefined>>;
    readonly?: boolean;
};

const radioLabels = [
    "drivingRangeC3",
    "drivingRangeC20",
    "drivingRangeC200",
    "drivingRangeCnoLimit",
    "none",
];

const yachtsLabels = ["motoryachts", "sailyachts"];

const mTextValues = ["M (3 nm)", "M (20 nm)", "M (200 nm)", "M (no limits)", ""];
const sTextValues = mTextValues.map(m => m.replace("M", "S"));

const validForToRadios = (text: string) => {
    let result = ["none", "none"];

    if (text) {
        for (let i = 0; i < mTextValues.length; i++) {
            if (text.includes(mTextValues[i])) {
                result[0] = radioLabels[i];
                break;
            }
        }

        for (let i = 0; i < sTextValues.length; i++) {
            if (text.includes(sTextValues[i])) {
                result[1] = radioLabels[i];
                break;
            }
        }
    }

    return result;
};

const radiosToValidFor = (radios: string[]) => {
    if (radios[0] === "none" && radios[1] === "none") {
        return "";
    }

    let result = "C";

    if (radios[0] && radios[0] !== "none") {
        result = `${result}, ${mTextValues[radioLabels.indexOf(radios[0])]}`;
    }

    if (radios[1] && radios[1] !== "none") {
        result = `${result}, ${sTextValues[radioLabels.indexOf(radios[1])]}`;
    }

    return result;
};

export const ICCorICSInputs = ({
    certificate,
    setCertificate,
    setAttachment1,
    setAttachment2,
    readonly = false,
}: Props) => {
    const intl = useIntl();
    const { countries } = useSelector((state: AppState) => state.nationalities);
    const examinationOrganizations = useSelector(
        (state: AppState) => state.examinationOrganizations
    );
    const paymentTypes = useSelector((state: AppState) => state.paymentTypes);
    const [radioSelections, setRadioSelections] = useState(validForToRadios(certificate.validFor));
    const [dateOfReceipt, setDateOfReceipt] = useState(
        certificate.dateOfReceipt ? deconstructDate(certificate.dateOfReceipt) : getTodayDate()
    );
    const [paymentDate, setPaymentDate] = useState(
        certificate.dateOfPayment ? deconstructDate(certificate.dateOfPayment) : dateInit
    );
    const [dateOfIssue, setDateOfIssue] = useState(
        certificate.dateOfIssue ? deconstructDate(certificate.dateOfIssue) : dateInit
    );
    const [originalAttachmentName1, setOriginalAttachmentName1] = useState<string>();
    const [originalAttachmentName2, setOriginalAttachmentName2] = useState<string>();

    const [firstAidOptions, setFirstAidOptions] = useState<SearchableIcs[]>([]);
    const [paymentAmountOptions, setPaymentAmountOptions] = useState<SearchableIcs[]>([]);

    const updateRadioSelections = (range: string, i: number) => {
        let newSelection = [...radioSelections];
        newSelection[i] = range;
        const validFor = radiosToValidFor(newSelection);
        setCertificate({ ...certificate, validFor });
    };

    const handleDeleteAttachment = async (guid: string, num: 1 | 2) => {
        await deleteAttachment(guid, num);
        if (num === 1) {
            setOriginalAttachmentName1(undefined);
            setCertificate({ ...certificate, nameOfAttachment1: undefined });
        } else {
            setOriginalAttachmentName2(undefined);
            setCertificate({ ...certificate, nameOfAttachment2: undefined });
        }
    };

    const onDrop = (attachmentNum: number, acceptedFile: File) => {
        if (acceptedFile) {
            acceptedFile = Array.isArray(acceptedFile) ? acceptedFile[0] : acceptedFile;
            if (attachmentNum === 1) {
                setAttachment1!(acceptedFile);
                setCertificate({ ...certificate, nameOfAttachment1: acceptedFile.name });
            } else {
                setAttachment2!(acceptedFile);
                setCertificate({ ...certificate, nameOfAttachment2: acceptedFile.name });
            }
        } else {
            toast(intl.formatMessage({ id: "invalidFileFormat" }), false);
        }
    };

    const getSearchableIcs = useCallback(async () => {
        setLoading(true);
        const response = await getSearchableIcsProps();
        setFirstAidOptions(response.firstAidCourse);
        setPaymentAmountOptions(response.paymentAmount);
        setLoading(false);
    }, []);

    useEffect(() => {
        setOriginalAttachmentName1(certificate.nameOfAttachment1);
        setOriginalAttachmentName2(certificate.nameOfAttachment2);
    }, []);

    useEffect(() => {
        setRadioSelections(validForToRadios(certificate.validFor));
    }, [certificate.validFor]);

    useEffect(() => {
        const dateString = constructDate(dateOfReceipt);
        setCertificate({
            ...certificate,
            dateOfReceipt: dateString.startsWith("-") ? "" : dateString,
        });
    }, [dateOfReceipt]);

    useEffect(() => {
        const dateString = constructDate(paymentDate);
        setCertificate({
            ...certificate,
            dateOfPayment: dateString.startsWith("-") ? "" : dateString,
        });
    }, [paymentDate]);

    useEffect(() => {
        if (
            certificate.type ===
                CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS &&
            !certificate.dateOfReceipt
        ) {
            setCertificate({ ...certificate, dateOfReceipt: constructDate(getTodayDate()) });
        }
    }, [certificate]);

    useEffect(() => {
        if (
            certificate.type ===
            CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS
        )
            getSearchableIcs();
    }, [getSearchableIcs, certificate.type]);

    const [loading, setLoading] = useState(false);

    const handleUpdateProps = (isFirstAid: boolean, value: string) => {
        if (isFirstAid)
            setFirstAidOptions(firstAidOptions => [
                ...firstAidOptions,
                { text: value, value: value, key: value },
            ]);
        else {
            let checkValue: string = value.replace(",", ".");
            if (!isNaN(Number(checkValue))) {
                checkValue = checkValue.replace(".", ",");
                setPaymentAmountOptions(paymentAmountOptions => [
                    ...paymentAmountOptions,
                    { text: value, value: value, key: value },
                ]);
            } else {
                setCertificate({ ...certificate, paymentAmount: "" });
            }
        }
    };

    return (
        <>
            {certificate.type !==
                CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS && (
                <div className="certificate-row four">
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "loadCapacity" })}</label>
                        <Select
                            selectOnBlur={false}
                            options={loadCapacityOptions}
                            value={certificate.loadCapacity}
                            onChange={(e, data: any) => {
                                setCertificate({ ...certificate, loadCapacity: +data.value });
                            }}
                        />
                    </section>
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "carryCapValue" })}</label>
                        <Input
                            value={certificate.carryingCapacityValue ?? ""}
                            onChange={(e: any) =>
                                setCertificate({
                                    ...certificate,
                                    carryingCapacityValue: e.target.value,
                                })
                            }
                        />
                    </section>
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "drivePower" })}</label>
                        <Select
                            selectOnBlur={false}
                            options={drivePowerOptions}
                            value={certificate.drivePower}
                            onChange={(e, data: any) => {
                                setCertificate({ ...certificate, drivePower: +data.value });
                            }}
                        />
                    </section>
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "drivePowerValue" })}</label>
                        <Input
                            value={certificate.drivePowerValue ?? ""}
                            onChange={(e: any) =>
                                setCertificate({
                                    ...certificate,
                                    drivePowerValue: e.target.value,
                                })
                            }
                        />
                    </section>
                </div>
            )}
            <div className="certificate-row four">
                {certificate.type !==
                    CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS && (
                    <section className="input-container">
                        <label>
                            {intl.formatMessage({ id: "vehicleLength" })}
                            <span className="input-required">*</span>
                        </label>
                        <Select
                            selectOnBlur={false}
                            options={TranslateProps(vehicleLengthOptions)}
                            value={certificate.vehicleLength}
                            onChange={(e, data: any) => {
                                setCertificate({ ...certificate, vehicleLength: +data.value });
                            }}
                        />
                    </section>
                )}
                <section
                    className={`input-container ${
                        certificate.type ===
                        CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS
                            ? "row-span-three-first"
                            : ""
                    }`}
                >
                    <label>
                        {intl.formatMessage({ id: "validFor" })}
                        <span className="input-required">*</span>
                    </label>
                    {certificate.type ===
                    CertificateType.InternationalCertificateForOperatorsOfPleasureCraft ? (
                        <Input
                            value={certificate.validFor ?? ""}
                            onChange={(e: any) =>
                                setCertificate({
                                    ...certificate,
                                    validFor: e.target.value,
                                })
                            }
                        />
                    ) : (
                        <div className={`ics-valid-for-container ${readonly ? "disabled" : ""}`}>
                            {yachtsLabels.map((yacht, i) => (
                                <div className="radio-row">
                                    <label>{intl.formatMessage({ id: yacht })}</label>
                                    {radioLabels.map(range => (
                                        <Radio
                                            label={intl.formatMessage({ id: range })}
                                            name="radioGroup"
                                            value={range}
                                            checked={radioSelections[i] === range}
                                            onChange={() => updateRadioSelections(range, i)}
                                            disabled={readonly}
                                        />
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                </section>
                {certificate.type ===
                    CertificateType.InternationalCertificateForOperatorsOfPleasureCraft && (
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "conditionsType" })}</label>
                        <Select
                            multiple
                            search
                            placeholder="--"
                            options={TranslateProps(conditionOptions)}
                            value={certificate.conditions}
                            onChange={(e, data: any) =>
                                setCertificate({ ...certificate, conditions: data.value })
                            }
                        />
                    </section>
                )}
                {certificate.type ===
                    CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS &&
                    dateOfIssue && (
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "issueDate" })}</label>
                            <DateInput date={dateOfIssue} disabled setDate={setDateOfIssue} />
                        </section>
                    )}
            </div>
            {certificate.type ===
                CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS && (
                <>
                    <div className="certificate-row flex">
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "applicationCategory" })}</label>
                            <Select
                                selectOnBlur={false}
                                options={TranslateProps(applicationCategoryOptions)}
                                value={certificate.applicationCategory}
                                disabled={readonly}
                                onChange={(e, data: any) => {
                                    setCertificate({
                                        ...certificate,
                                        applicationCategory: +data.value,
                                    });
                                }}
                            />
                        </section>
                        <section className="input-container">
                            <label>
                                {intl.formatMessage({ id: "stateOfBirth" })}
                                <span className="input-required">*</span>
                            </label>
                            <Select
                                selectOnBlur={false}
                                search
                                options={TranslateCountryProps(countries, intl.locale)}
                                value={certificate.stateOfBirth}
                                disabled={readonly}
                                onChange={(e, data: any) =>
                                    setCertificate({
                                        ...certificate,
                                        stateOfBirth: +data.value,
                                    })
                                }
                            />
                        </section>
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "examinationOrganization" })}</label>
                            <Select
                                selectOnBlur={false}
                                search
                                clearable
                                options={examinationOrPaymentToDropdownProp(
                                    examinationOrganizations
                                )}
                                value={certificate.examinationOrganization}
                                disabled={readonly}
                                onChange={(e, data: any) =>
                                    setCertificate({
                                        ...certificate,
                                        examinationOrganization: data.value,
                                    })
                                }
                            />
                        </section>
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "firstAidCourse" })}</label>
                            <Select
                                search
                                allowAdditions
                                additionLabel=""
                                clearable
                                loading={loading}
                                options={firstAidOptions}
                                value={certificate.firstAidCourse}
                                disabled={readonly}
                                onChange={(e, data: any) =>
                                    setCertificate({
                                        ...certificate,
                                        firstAidCourse: data.value,
                                    })
                                }
                                onAddItem={(e, data: any) => {
                                    handleUpdateProps(true, data.value);
                                }}
                            />
                        </section>
                        <section className="input-container small">
                            <label>{intl.formatMessage({ id: "canceled" })}</label>
                            <Checkbox
                                toggle
                                disabled={!!certificate.batchId}
                                checked={certificate.canceled}
                                onChange={(_, data: any) => {
                                    setCertificate({
                                        ...certificate,
                                        canceled: data.checked,
                                    });
                                }}
                            />
                        </section>
                    </div>
                    <div className="certificate-row four">
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "dateOfReceipt" })}</label>
                            <DateInput
                                date={dateOfReceipt}
                                setDate={setDateOfReceipt}
                                extendedYears
                                disabled={readonly}
                            />
                        </section>
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "paymentDate" })}</label>
                            <DateInput
                                clearable
                                date={paymentDate}
                                setDate={setPaymentDate}
                                disabled={readonly}
                            />
                        </section>
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "paymentAmount" })}</label>
                            <Select
                                search
                                allowAdditions
                                additionLabel=""
                                clearable
                                loading={loading}
                                options={paymentAmountOptions}
                                value={certificate.paymentAmount?.toString().replace(".", ",")}
                                disabled={readonly}
                                onChange={(e, data: any) =>
                                    setCertificate({
                                        ...certificate,
                                        paymentAmount: data.value.replace(",", "."),
                                    })
                                }
                                onAddItem={(e, data: any) => {
                                    handleUpdateProps(false, data.value);
                                }}
                            />
                        </section>
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "paymentType" })}</label>
                            <Select
                                selectOnBlur={false}
                                search
                                clearable
                                options={examinationOrPaymentToDropdownProp(paymentTypes)}
                                value={certificate.paymentType}
                                disabled={readonly}
                                onChange={(e, data: any) =>
                                    setCertificate({
                                        ...certificate,
                                        paymentType: data.value,
                                    })
                                }
                            />
                        </section>
                    </div>

                    <Divider horizontal>{intl.formatMessage({ id: "attachments" })}</Divider>

                    <div className="certificate-row two">
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "firstAttachment" })}</label>
                            <div className="flex-row">
                                <FileDropzone
                                    handleSetFile={onDrop.bind(null, 1)}
                                    msg={
                                        certificate.nameOfAttachment1 ??
                                        intl.formatMessage({ id: "dragAndDrop" })
                                    }
                                    accept={{
                                        "image/*": [],
                                        "application/pdf": [],
                                        "application/msword": [],
                                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                                            [],
                                    }}
                                />
                                {originalAttachmentName1 &&
                                    originalAttachmentName1 === certificate.nameOfAttachment1 && (
                                        <>
                                            <a
                                                title="Download"
                                                href={`/api/certificates/attachments/${certificate.guid}/1`}
                                            >
                                                <Icon name="download" size="big" />
                                            </a>
                                            <Icon
                                                title={intl.formatMessage({ id: "delete" })}
                                                name="x"
                                                size="big"
                                                onClick={() =>
                                                    handleDeleteAttachment(certificate.guid, 1)
                                                }
                                            />
                                        </>
                                    )}
                            </div>
                        </section>

                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "secondAttachment" })}</label>
                            <div className="flex-row">
                                <FileDropzone
                                    handleSetFile={onDrop.bind(null, 2)}
                                    msg={
                                        certificate.nameOfAttachment2 ??
                                        intl.formatMessage({ id: "dragAndDrop" })
                                    }
                                    accept={{
                                        "image/*": [],
                                        "application/pdf": [],
                                        "application/msword": [],
                                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                                            [],
                                    }}
                                />
                                {originalAttachmentName2 &&
                                    originalAttachmentName2 === certificate.nameOfAttachment2 && (
                                        <>
                                            <a
                                                title="Download"
                                                href={`/api/certificates/attachments/${certificate.guid}/2`}
                                            >
                                                <Icon name="download" size="big" />
                                            </a>
                                            <Icon
                                                title={intl.formatMessage({ id: "delete" })}
                                                name="x"
                                                size="big"
                                                onClick={() =>
                                                    handleDeleteAttachment(certificate.guid, 2)
                                                }
                                            />
                                        </>
                                    )}
                            </div>
                        </section>
                    </div>
                </>
            )}
        </>
    );
};
